<template>
  <!--
    EL COBRO EN EL DATÁFONO, EN DIRECTO

    Se abre al mandar un cobro al datáfono desde «Agregar pago» (o al pulsar una línea que
    sigue en el datáfono). Pregunta al servidor cada segundo y medio en qué va:

      pendiente      → «Enviando al datáfono…»          (se puede cancelar)
      enviado        → «Pide al huésped que pase la tarjeta»
      cobrado        → ✓ con la marca de la tarjeta y la autorización
      rechazado / cancelado / error / caducado → qué pasó, y «Volver a intentar»
      sin_respuesta  → el datáfono no devolvió el resultado: PUEDE que se cobrara.
                       Se pregunta al anfitrión qué ve en el aparato. Nunca se da por
                       fallido solo: eso llevaría a cobrar dos veces.
  -->
  <div class="cd-capa" @click.self="intentarCerrar">
    <div class="cd-modal" role="dialog" aria-modal="true" aria-labelledby="cd-titulo" aria-live="polite">
      <div class="cd-estado" :class="'cd-estado--' + tono" aria-hidden="true">
        <span v-if="enCurso" class="cd-girando"></span>
        <svg v-else-if="estado === 'cobrado'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
        <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 8v4.5M12 16h.01" /></svg>
      </div>

      <p class="cd-datafono">{{ nombreDatafono }}</p>
      <h3 id="cd-titulo" class="cd-titulo">{{ titulo }}</h3>
      <p class="cd-importe">{{ importe }}</p>
      <p class="cd-texto">{{ texto }}</p>

      <p v-if="estado === 'cobrado' && (cobro.tarjeta || cobro.codigo_autorizacion)" class="cd-detalle">
        <span v-if="cobro.tarjeta">{{ cobro.tarjeta }}</span>
        <span v-if="cobro.codigo_autorizacion">{{ $t("datafono.cobro.authCode", { code: cobro.codigo_autorizacion }) }}</span>
      </p>

      <p v-if="error" class="cd-error" role="alert">{{ error }}</p>

      <div class="cd-acciones">
        <template v-if="estado === 'pendiente'">
          <button type="button" class="cd-btn cd-btn--secundario" :disabled="ocupado" @click="cancelar">{{ $t("datafono.cobro.cancel") }}</button>
        </template>
        <template v-else-if="estado === 'enviado'">
          <button type="button" class="cd-btn cd-btn--secundario" @click="$emit('close')">{{ $t("datafono.cobro.hide") }}</button>
        </template>
        <template v-else-if="estado === 'sin_respuesta'">
          <button type="button" class="cd-btn cd-btn--secundario" :disabled="ocupado" @click="resolver(false)">{{ $t("datafono.cobro.notCharged") }}</button>
          <button type="button" class="cd-btn cd-btn--primario" :disabled="ocupado" @click="resolver(true)">{{ $t("datafono.cobro.wasCharged") }}</button>
        </template>
        <template v-else>
          <button v-if="sePuedeReintentar" type="button" class="cd-btn cd-btn--secundario" @click="$emit('retry', cobro.importe)">{{ $t("datafono.cobro.retry") }}</button>
          <button type="button" class="cd-btn cd-btn--primario" @click="$emit('close')">{{ $t("datafono.cobro.close") }}</button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { formatMoneyCode } from "@/utils/formatters";

const FINALES = ["cobrado", "rechazado", "cancelado", "error", "caducado"];

export default {
  name: "CobroEnDatafonoModal",

  props: {
    cobroInicial: { type: Object, required: true },
  },

  data() {
    return {
      cobro: Object.assign({}, this.cobroInicial),
      ocupado: false,
      error: "",
      reloj: null,
      avisado: false,
    };
  },

  computed: {
    estado() {
      return this.cobro.estado;
    },
    enCurso() {
      return this.estado === "pendiente" || this.estado === "enviado";
    },
    tono() {
      if (this.estado === "cobrado") return "ok";
      if (this.enCurso) return "curso";
      if (this.estado === "sin_respuesta") return "aviso";
      return "mal";
    },
    nombreDatafono() {
      return (this.cobro.terminal && this.cobro.terminal.nombre) || this.$t("datafono.cobro.terminal");
    },
    importe() {
      return formatMoneyCode(this.cobro.importe, this.cobro.moneda || "EUR");
    },
    titulo() {
      return this.$t(`datafono.cobro.title.${this.estado}`);
    },
    texto() {
      // El mensaje del servidor manda (dice el motivo exacto); si no hay, el genérico.
      if (!this.enCurso && this.cobro.mensaje && this.estado !== "cobrado") return this.cobro.mensaje;
      return this.$t(`datafono.cobro.text.${this.estado}`);
    },
    sePuedeReintentar() {
      return ["rechazado", "cancelado", "error", "caducado"].indexOf(this.estado) !== -1;
    },
  },

  mounted() {
    this.reloj = setInterval(this.consultar, 1500);
    window.addEventListener("keydown", this.alPulsarTecla);
  },

  beforeDestroy() {
    clearInterval(this.reloj);
    window.removeEventListener("keydown", this.alPulsarTecla);
  },

  methods: {
    async consultar() {
      if (!this.enCurso) return;
      try {
        const { data } = await axios.get(`/me/cobros-datafono/${this.cobro.uuid}`);
        this.aplicar(data && data.data);
      } catch (e) {
        // Un fallo de red puntual no cambia nada: se sigue preguntando.
      }
    },

    aplicar(nuevo) {
      if (!nuevo) return;
      const antes = this.cobro.estado;
      this.cobro = Object.assign({}, this.cobro, nuevo);
      if (antes !== nuevo.estado && (FINALES.indexOf(nuevo.estado) !== -1 || nuevo.estado === "sin_respuesta")) {
        // Avisa a la ficha para que recargue saldos y líneas.
        this.$emit("finished", this.cobro);
      }
    },

    async cancelar() {
      this.ocupado = true;
      this.error = "";
      try {
        const { data } = await axios.post(`/me/cobros-datafono/${this.cobro.uuid}/cancelar`);
        this.aplicar(data && data.data);
      } catch (e) {
        const r = e && e.response;
        if (r && r.data && r.data.data) this.aplicar(r.data.data);
        this.error = (r && r.data && r.data.message) || this.$t("datafono.cobro.genericError");
      } finally {
        this.ocupado = false;
      }
    },

    async resolver(seCobro) {
      this.ocupado = true;
      this.error = "";
      try {
        const { data } = await axios.post(`/me/cobros-datafono/${this.cobro.uuid}/resolver`, { se_cobro: seCobro });
        this.aplicar(data && data.data);
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.message) || this.$t("datafono.cobro.genericError");
      } finally {
        this.ocupado = false;
      }
    },

    intentarCerrar() {
      // Mientras está pendiente de enviar, cerrar no lo cancela: hay que hacerlo a propósito.
      if (this.estado === "pendiente") return;
      this.$emit("close");
    },

    alPulsarTecla(e) {
      if (e.key === "Escape") this.intentarCerrar();
    },
  },
};
</script>

<style scoped>
.cd-capa {
  position: fixed;
  inset: 0;
  z-index: 2100;
  background: rgba(15, 23, 42, 0.36);
  display: flex;
  padding: 16px;
  overflow-y: auto;
  overscroll-behavior: contain;
  font-family: "DM Sans", system-ui, -apple-system, "Segoe UI", sans-serif;
}
.cd-modal {
  margin: auto;   /* centrada si cabe; si no, empieza arriba y la capa se desplaza */
  width: 100%;
  max-width: 380px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 16px 48px rgba(15, 23, 42, 0.14);
  padding: 28px 24px 20px;
  text-align: center;
}
.cd-estado {
  width: 52px;
  height: 52px;
  margin: 0 auto 14px;
  border-radius: 50%;
  border: 1px solid #e2e8f0;
  display: grid;
  place-items: center;
  color: #475569;
}
.cd-estado svg { width: 24px; height: 24px; }
.cd-estado--ok { color: #15803d; border-color: #bbf7d0; }
.cd-estado--mal { color: #b91c1c; border-color: #fecaca; }
.cd-estado--aviso { color: #b45309; border-color: #fde68a; }
.cd-girando {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2.5px solid #cbd5e1;
  border-top-color: #1e293b;
  animation: cd-g 0.8s linear infinite;
}
@keyframes cd-g { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .cd-girando { animation: none; } }
.cd-datafono {
  margin: 0 0 4px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #64748b;
}
.cd-titulo { margin: 0 0 10px; font-size: 17px; font-weight: 700; color: #0f172a; }
.cd-importe {
  margin: 0 0 10px;
  font-size: 30px;
  font-weight: 700;
  letter-spacing: -0.6px;
  font-variant-numeric: tabular-nums;
  color: #0f172a;
}
.cd-texto { margin: 0; font-size: 13.5px; line-height: 1.55; color: #475569; }
.cd-detalle {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px 12px;
  margin: 12px 0 0;
  font-size: 12.5px;
  color: #64748b;
}
.cd-error { margin: 12px 0 0; font-size: 13px; color: #b91c1c; }
.cd-acciones { display: flex; justify-content: center; gap: 8px; margin-top: 22px; flex-wrap: wrap; }
.cd-btn {
  font: inherit;
  font-size: 13.5px;
  font-weight: 600;
  border-radius: 9px;
  padding: 9px 16px;
  cursor: pointer;
  transition: background-color 0.15s, opacity 0.15s;
}
.cd-btn:disabled { opacity: 0.5; cursor: default; }
.cd-btn:focus-visible { outline: 2px solid #673de6; outline-offset: 2px; }
.cd-btn--primario { border: 0; background: #1e293b; color: #fff; }
.cd-btn--primario:not(:disabled):hover { background: #0f172a; }
.cd-btn--secundario { border: 1px solid #e2e8f0; background: #fff; color: #1e293b; }
.cd-btn--secundario:not(:disabled):hover { background: #f8fafc; }
</style>
